import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { SignOut, CaretDown, X, Vault, UsersThree } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/permissions";
import { getModuleMenus } from "@/lib/moduleMenus";

const NavItem = ({ to, label, icon: Icon, testId, end, indent = false }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-3 ${indent ? "pl-8 pr-3" : "px-3"} py-2 text-sm transition-colors duration-200 border-l-2 ${
        isActive
          ? "bg-white/10 text-white border-[#95C11F]"
          : "border-transparent text-slate-300 hover:text-white hover:bg-white/5"
      }`
    }
  >
    <Icon size={18} weight="duotone" />
    <span>{label}</span>
  </NavLink>
);

/**
 * NavGroup — açılır/kapanır grup başlığı + alt öğeler.
 */
const NavGroup = ({ title, icon: Icon, testId, items, currentPath }) => {
  const isChildActive = items.some((it) =>
    !it.to ? false : (it.end ? currentPath === it.to : currentPath === it.to || currentPath.startsWith(it.to + "/"))
  );
  const [open, setOpen] = React.useState(false);

  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-3 py-2.5 text-[11px] uppercase tracking-[0.2em] font-bold text-white hover:bg-white/5 transition-colors"
      >
        <span className="flex items-center gap-2">
          <Icon size={14} weight="fill" className={isChildActive ? "text-[#95C11F]" : "text-[#95C11F]"} />
          {title}
        </span>
        <CaretDown
          size={12}
          weight="bold"
          className={`transition-transform duration-200 ${open ? "rotate-0" : "-rotate-90"}`}
        />
      </button>
      <div
        className={`grid transition-all duration-200 ease-out ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="space-y-0.5 pb-1">
            {items.length === 0 ? (
              <div className="pl-8 pr-3 py-2 text-xs italic text-slate-400" data-testid={`${testId}-empty`}>
                Yakında...
              </div>
            ) : (
              items.map((it) => <NavItem key={it.to} {...it} indent />)
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Sidebar({ open = false, onClose }) {
  const { user, logout, financialAccess, kasaAccess, ortakAccess } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isAdmin = user?.role === "yonetici";
  const modules = getModuleMenus(isAdmin);

  return (
    <aside
      className={`fixed inset-y-0 left-0 w-64 bg-[#1B4776] text-slate-200 flex flex-col z-50 transition-transform duration-300 ease-out lg:translate-x-0 ${
        open ? "translate-x-0 shadow-2xl" : "-translate-x-full"
      }`}
      data-testid="sidebar"
    >
      <div className="px-6 py-6 lg:py-8 border-b border-white/10 flex items-start justify-between">
        <button
          type="button"
          onClick={() => { nav("/"); onClose?.(); }}
          data-testid="sidebar-logo-home"
          className="text-left group cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#95C11F] rounded-sm"
          aria-label="Ana sayfaya git"
        >
          <img
            src="/mss-logo.png"
            alt="MSS"
            className="h-auto w-[180px] transition-opacity group-hover:opacity-80"
            style={{ filter: "brightness(0) invert(1)" }}
          />
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#95C11F] group-hover:text-white transition-colors">
              Yönetim Sistemi
            </div>
          </div>
        </button>
        {/* Mobil kapat butonu */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Menüyü kapat"
          data-testid="sidebar-close"
          className="lg:hidden -mr-2 p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-sm"
        >
          <X size={20} weight="bold" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-3">
        {modules.map((m) => (
          <NavGroup
            key={m.slug}
            title={m.title}
            icon={m.icon}
            testId={m.testId}
            items={m.items}
            currentPath={loc.pathname}
          />
        ))}
        {/* Finansal Kontrol Merkezi — SADECE whitelist'teki kullanıcılara görünür.
             Hard-coded yetkilendirme ile korunur; diğer herkeste tamamen gizli. */}
        {(financialAccess?.has_access || kasaAccess?.has_access || ortakAccess?.has_access) && (
          <div className="mt-4 pt-3 border-t border-white/10">
            <div className="px-3 mb-1 text-[9px] uppercase tracking-[0.3em] font-bold text-[#95C11F]/70">
              Gizli Erişim
            </div>
            {financialAccess?.has_access && (
              <NavLink
                to="/finansal-kontrol"
                data-testid="nav-financial-control"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors border-l-2 ${
                    isActive
                      ? "bg-emerald-500/10 text-white border-emerald-400"
                      : "border-transparent text-slate-300 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <Vault size={16} weight="duotone" className="text-emerald-400" />
                <span className="font-bold uppercase tracking-widest text-[11px]">
                  Finansal Kontrol
                </span>
              </NavLink>
            )}
            {kasaAccess?.has_access && (
              <NavLink
                to="/gayri-resmi-kasa"
                data-testid="nav-gayri-resmi-kasa"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors border-l-2 ${
                    isActive
                      ? "bg-amber-500/10 text-white border-amber-400"
                      : "border-transparent text-slate-300 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <Vault size={16} weight="duotone" className="text-amber-400" />
                <span className="font-bold uppercase tracking-widest text-[11px]">
                  Gayri Resmi Kasa
                </span>
              </NavLink>
            )}
            {ortakAccess?.has_access && (
              <NavLink
                to="/ortaklar-carisi"
                data-testid="nav-ortaklar-carisi"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors border-l-2 ${
                    isActive
                      ? "bg-sky-500/10 text-white border-sky-400"
                      : "border-transparent text-slate-300 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <UsersThree size={16} weight="duotone" className="text-sky-400" />
                <span className="font-bold uppercase tracking-widest text-[11px]">
                  Ortaklar Carisi
                </span>
              </NavLink>
            )}
          </div>
        )}
      </nav>

      <div className="p-4 border-t border-white/10">
        <div className="text-xs text-white mb-1 truncate font-semibold" data-testid="user-name">{user?.name}</div>
        <div className="text-[10px] tracking-[0.2em] text-[#95C11F] mb-3 font-bold" style={{ textTransform: "none" }}>
          {(ROLE_LABELS[user?.role] || user?.role || "").toLocaleUpperCase("tr-TR")}
        </div>
        <button
          data-testid="logout-btn"
          onClick={async () => { await logout(); nav("/login"); }}
          className="w-full flex items-center justify-center gap-2 py-2 text-sm bg-white/10 hover:bg-white/20 transition-colors"
        >
          <SignOut size={16} />
          Çıkış Yap
        </button>
      </div>
    </aside>
  );
}
